import { Link } from 'react-router-dom';

import { styled as colorStyled } from '@mui/system';
import {
  styled,
  IconButton,
  Drawer,
  Avatar,
  ListItemButton,
  ListItem,
  Box,
  Link as MuiLink,
  LinkProps,
  Stack,
} from '@mui/material';
import { AppBar as MuiAppBar } from '@mui/material';
import { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';

type StyledLinkProps = LinkProps & {
  to?: string;
  component?: typeof Link;
};

export const sidebarWidth = 240;

export const SidebarRoot = styled('div')({
  display: 'flex',
});

export const AppBarRoot = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<MuiAppBarProps & { open?: boolean }>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${sidebarWidth}px)`,
    marginLeft: `${sidebarWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),

    [theme.breakpoints.down('md')]: {
      width: `100%`,
      marginLeft: 0,
    },
  }),
}));

export const SidebarToggleButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ theme, open }) => ({
  marginRight: theme.spacing(2),
  ...(open && {
    display: 'none',
  }),
}));

export const TopBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

export const Navigation = styled(Drawer)({
  width: sidebarWidth,
  flexShrink: 0,

  '& .MuiDrawer-paper': {
    width: sidebarWidth,
    boxSizing: 'border-box',
  },
});

export const AccountAvatar = styled(Avatar)({
  width: 24,
  height: 24,
});

export const LicensingTermsAvatar = styled(Avatar)({
  width: 32,
  height: 32,
});

export const StyledListItemButton = styled(ListItemButton)({
  overflow: 'hidden',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

export const ConversationLink = styled(Link)({
  textDecoration: 'none',
  color: 'inherit',
});

export const StyledListItem = colorStyled(ListItem, {
  shouldForwardProp: (prop) =>
    prop !== 'isSelectedItem' && prop !== 'isNestedList',
})<{
  isSelectedItem: boolean;
  isNestedList?: boolean;
}>(({ theme, isSelectedItem, isNestedList }) => ({
  ...(isSelectedItem && {
    backgroundColor: theme.palette.primary[900],
  }),
  ...(isNestedList && {
    marginLeft: '20px',
  }),
  '&:hover': {
    backgroundColor: theme.palette.primary[900],
  },
  transition: 'background-color 0.2s ease-in-out',
}));

export const SidebarContentWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
});

export const LicensingTermsLink = styled(MuiLink)<StyledLinkProps>({
  fontSize: '12px',
});

export const LicensingTermsWrapper = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: '64px'
});
